import platform, { analytics } from '@play-co/gcinstant';
import { MovieClip } from '@play-co/timestep-core/ui';
import { getPet } from 'src/game/pet';
import { eventGTM, trackPetSpawn } from 'src/lib/analytics/events';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import {
  getCreativeSuffix,
  getSceneManager,
  waitForAssets,
  waitForItPromise,
} from 'src/lib/utils';
import { petCrateAssets, petSpeciesAssets } from 'src/loadingGroups';
import { Pet } from 'src/replicant/buruburu/pet';
import { Tutorial } from 'src/replicant/buruburu/tutorial';
import { TutorialStep } from 'src/replicant/buruburu/tutorial/tutorial.ruleset';
import { setPetCrateOpening } from 'src/state/ui';
import StateObserver from 'src/StateObserver';
import { showTutorial } from '../tutorial.util';
import { TutorialEvent } from '../TutorialController';
import { TutorialHandler } from './TutorialHandler';

export class BasicGameplayTutorial implements TutorialHandler {
  public readonly step: TutorialStep = 'basicGameplay';

  public async handleEvent(event: TutorialEvent): Promise<boolean> {
    // Event not relevant to this tutorial.
    if (event !== 'gameStarted') return false;

    let { user } = StateObserver.getState();

    // Create a new pet if doen't exist yet, to avoid duplicated pets on reload after accepting ToS
    if (!Pet.getters(user).getCurrentEvolution()) {
      await this.createFirstPet();
    }

    // Initialize existing pet
    await this.initPet();

    return true;
  }

  /**
   * Create the first pet, showing ToS popup and triggering first TutorialStart event
   */
  private async createFirstPet() {
    eventGTM('TutorialStart');
    analytics.pushEvent('TutorialStart', {
      version: Tutorial.ruleset.version,
    });

    const openBoxStepAnalytics = {
      $stepIndex: 0,
      $stepName: 'open_box',
    };
    analytics.pushEvent('TutorialStepStart', openBoxStepAnalytics);

    await waitForAssets(petCrateAssets);
    const { height } = StateObserver.getState().ui.screenSize;
    const petCrate = new MovieClip({
      superview: getSceneManager().scenes.livingRoom.getView(),
      x: 365,
      y: height * 0.618,
      centerOnOrigin: true,
      zIndex: 1,
      url: 'assets/tutorial/crate',
      defaultAnimation: 'pet_crate_idle',
      offsetX: 170,
      offsetY: 124,
    });

    await openPopupPromise('popupAcceptToS', { showTos: true });

    StateObserver.dispatch(setPetCrateOpening(true));
    await StateObserver.invoke.createNewPet();
    await StateObserver.invoke.schedulePetActivity();
    analytics.pushEvent('TutorialStepFinish', openBoxStepAnalytics);
    trackPetSpawn({ feature: 'new_game', subFeature: 'new_game' });

    const { user } = StateObserver.getState();
    const { species } = Pet.getters(user).getCurrentEvolution();
    await waitForAssets(petSpeciesAssets[species]);

    petCrate.play('pet_crate_open', () => {
      petCrate.removeFromSuperview();
      StateObserver.dispatch(setPetCrateOpening(false));
    });

    // Show pet during crate opening animation
    await waitForItPromise(1250);
  }

  /**
   * Initialise existing pet, start wandering and show basic gameplay tutorial
   */
  private async initPet() {
    const { user } = StateObserver.getState();
    const { species } = Pet.getters(user).getCurrentEvolution();
    const { position, appearance } = getPet();

    appearance.setSpecies(species);
    void position.moveTo({ scene: 'livingRoom', location: 'tc' });

    const evolution = Pet.ruleset.evolutions[species];
    const message = evolution[`greetings_${getCreativeSuffix()}`];
    await openPopupPromise('popupSpeechBubble', { message });

    position.setWandering(true);

    await showTutorial('basicGameplay', {
      $stepIndex: 1,
      $stepName: 'basic_gameplay_popup',
    });
  }
}
